import { XIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

export interface FormPanelProps {
  title: string;
  description?: string;
  children: ReactNode;
  onSubmit?: (e: React.FormEvent) => void;
  submitLabel?: string;
  cancelLabel?: string;
  isSubmitting?: boolean;
  submitError?: string | null;
  className?: string;
  showFooter?: boolean;
  onCancel?: () => void;
}

export function FormPanel({
  title,
  description,
  children,
  onSubmit,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isSubmitting = false,
  submitError,
  className,
  showFooter = true,
  onCancel,
}: FormPanelProps) {
  const body = (
    <>
      <div className="flex shrink-0 items-start gap-2 border-b border-border-default px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
          {description ? <p className="mt-1 text-sm text-text-secondary">{description}</p> : null}
        </div>
        {/* Always available, unlike the footer Cancel button which is hidden in read-only mode. */}
        {onCancel ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="-mr-1 shrink-0"
            aria-label="Close"
            onClick={onCancel}
          >
            <XIcon />
          </Button>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">{children}</div>
      {submitError ? (
        <Alert variant="destructive" className="mx-4 mb-4">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      ) : null}
      {showFooter ? (
        <div className="flex shrink-0 justify-end gap-2 border-t border-border-default px-4 py-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? <Spinner data-icon="inline-start" /> : null}
            {isSubmitting ? 'Saving...' : submitLabel}
          </Button>
        </div>
      ) : null}
    </>
  );

  const containerClass = cn('flex h-full flex-col', className);

  if (onSubmit) {
    return (
      <form onSubmit={onSubmit} className={containerClass}>
        {body}
      </form>
    );
  }

  return <div className={containerClass}>{body}</div>;
}
